import {
    type ChangeEvent,
    type ChangeEventHandler,
    FormEvent,
    useState
} from "react";
import {
    Button,
    ButtonColor,
    ButtonType,
    Col,
    Column,
    Container,
    ContainerSize,
    Form,
    FormControlType,
    Heading,
    Row,
    Text
} from "@rebootui";
import CodeSnippet from "../../../components/controls/CodeSnippet";
import CodePreview from "../../../components/controls/CodePreview";
import CodeHighlighter from "../../../components/controls/CodeHighlighter";


type ValidationPageProps = {}

const ValidationPage = ({ }: ValidationPageProps) => {

    const [validated, setValidated] = useState(false);
    const [formData, setFormData] = useState({
        firstName: 'Mark',
        lastName: 'Otto',
        username: '',
        city: '',
        state: '',
        zip: '',
        terms: false
    });

    const handleFormFieldChange = (
        e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        const { name, value, type } = e.target;
        if (type === "checkbox") {
            const checked = (e.target as HTMLInputElement).checked;
            setFormData((prev) => ({ ...prev, [name]: checked }));
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
    };

    const onControlChange =
        handleFormFieldChange as ChangeEventHandler<HTMLInputElement>;
    const onSelectChange =
        handleFormFieldChange as ChangeEventHandler<HTMLSelectElement>;

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const form = event.currentTarget;

        if (form.checkValidity() === false) {
            event.stopPropagation();
        } else {
            console.log('Form submitted:', formData);
        }

        setValidated(true);
    };

    // Advanced Custom Validation State
    const [advancedSuccess, setAdvancedSuccess] = useState(false);

    const [advancedErrors, setAdvancedErrors] = useState({
        phone: "",
        fName: "",
        lName: "",
        email: "",
    });

    const [advancedFormData, setAdvancedFormData] = useState({
        fName: "",
        lName: "",
        phone: "",
        email: "",
    });

    // Custom validation function
    const validateAdvancedForm = (values: typeof advancedFormData) => {
        const errors: Partial<Record<keyof typeof advancedFormData, string>> = {};

        if (!values.fName.trim()) {
            errors.fName = "First name is required.";
        } else if (values.fName.length < 2) {
            errors.fName = "First name must be at least 2 characters.";
        }

        if (!values.lName.trim()) {
            errors.lName = "Last name is required.";
        } else if (values.lName.length < 2) {
            errors.lName = "Last name must be at least 2 characters.";
        }

        if (!values.phone.trim()) {
            errors.phone = "Phone number is required.";
        } else if (!/^\d{10}$/.test(values.phone.replace(/\D/g, ''))) {
            errors.phone = "Please enter a valid 10-digit phone number.";
        }

        if (!values.email.trim()) {
            errors.email = "Email is required.";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
            errors.email = "Please enter a valid email address.";
        }

        return errors;
    };

    const handleAdvancedChange = (e: ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setAdvancedFormData((prev) => ({
            ...prev,
            [name]: value
        }));
        setAdvancedSuccess(false);
        setAdvancedErrors((prev) => ({
            ...prev,
            [name]: "",
        }));
    };

    const handleAdvancedBlur = (e: ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        const currentValues = { ...advancedFormData, [name]: value };
        const fieldErrors = validateAdvancedForm(currentValues);
        setAdvancedErrors((prev) => ({
            ...prev,
            [name]: fieldErrors[name as keyof typeof fieldErrors] ?? "",
        }));
    };

    // Handle advanced form submission
    const handleAdvancedSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const formErrors = validateAdvancedForm(advancedFormData);

        if (Object.keys(formErrors).length === 0) {
            console.log("Form submitted successfully:", advancedFormData);
            setAdvancedSuccess(true);
            setAdvancedFormData({
                fName: "",
                lName: "",
                phone: "",
                email: "",
            });
            setAdvancedErrors({
                phone: "",
                fName: "",
                lName: "",
                email: "",
            });
        } else {
            setAdvancedSuccess(false);
            setAdvancedErrors({
                phone: formErrors.phone ?? "",
                fName: formErrors.fName ?? "",
                lName: formErrors.lName ?? "",
                email: formErrors.email ?? "",
            });
        }
    };


    return (
        <Container size={ContainerSize.Fluid}>
            {/* Validation */}
            <Heading el="h1" className="fw-semibold mb-3 ls-1">
                Validation
            </Heading>
            <Text className="mb-4">
                Provide valuable, actionable feedback to your users with HTML5 form validation, via browser default behaviors or custom styles and JavaScript.
            </Text>

            <Heading el="h2" className="fw-medium fs-3 mt-4 mb-3">
                Custom styles
            </Heading>
            <Text className="mb-4">
                For custom Bootstrap form validation messages, you'll need to add the <Text el="code">noValidate</Text> boolean attribute to your <Text el="code">&lt;form&gt;</Text>. This disables the browser default feedback tooltips, but still provides access to the form validation APIs in JavaScript.
            </Text>
            <Text className="mb-4">
                Try to submit the form below; our JavaScript will intercept the submit button and relay feedback to you. When attempting to submit, you will see the <Text el="code">:invalid</Text> and <Text el="code">:valid</Text> styles applied to your form controls.
            </Text>
            <Text className="mb-4">
                For checkboxes, put <Text el="code">Form.Feedback</Text> <Text el="strong">inside</Text>{' '}
                <Text el="code">Form.Check</Text> as children so the message stays a sibling of the input inside{' '}
                <Text el="code">.form-check</Text>, matching{' '}
                <a
                    href="https://getbootstrap.com/docs/5.3/forms/validation/"
                    className="link-underline link-underline-opacity-0"
                    target="_blank"
                    rel="noreferrer"
                >
                    Bootstrap validation
                </a>{' '}
                (<Text el="code">.was-validated :invalid ~ .invalid-feedback</Text>).
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Form validated={validated} onSubmit={handleSubmit} noValidate>
                        <Row className="g-3">
                            <Column col={Col.ColMd4}>
                                <Form.Label htmlFor="validationCustom01">First name</Form.Label>
                                <Form.Control
                                    type={FormControlType.Text}
                                    id="validationCustom01"
                                    name="firstName"
                                    value={formData.firstName}
                                    onChange={onControlChange}
                                    required
                                />
                                <Form.Feedback type="valid">
                                    Looks good!
                                </Form.Feedback>
                                <Form.Feedback type="invalid">
                                    First name is required.
                                </Form.Feedback>
                            </Column>
                            <Column col={Col.ColMd4}>
                                <Form.Label htmlFor="validationCustom02">Last name</Form.Label>
                                <Form.Control
                                    type={FormControlType.Text}
                                    id="validationCustom02"
                                    name="lastName"
                                    value={formData.lastName}
                                    onChange={onControlChange}
                                    required
                                />
                                <Form.Feedback type="valid">
                                    Looks good!
                                </Form.Feedback>
                                <Form.Feedback type="invalid">
                                    Last name is required.
                                </Form.Feedback>
                            </Column>
                            <Column col={Col.ColMd4}>
                                <Form.Label htmlFor="validationCustomUsername">Username</Form.Label>
                                <Form.Control
                                    type={FormControlType.Text}
                                    id="validationCustomUsername"
                                    name="username"
                                    value={formData.username}
                                    onChange={onControlChange}
                                    required
                                />
                                <Form.Feedback type="invalid">
                                    Please choose a username.
                                </Form.Feedback>
                            </Column>
                            <Column col={Col.ColMd6}>
                                <Form.Label htmlFor="validationCustom03">City</Form.Label>
                                <Form.Control
                                    type={FormControlType.Text}
                                    id="validationCustom03"
                                    name="city"
                                    value={formData.city}
                                    onChange={onControlChange}
                                    required
                                />
                                <Form.Feedback type="invalid">
                                    Please provide a valid city.
                                </Form.Feedback>
                            </Column>
                            <Column col={Col.ColMd3}>
                                <Form.Label htmlFor="validationCustom04">State</Form.Label>
                                <Form.Select
                                    id="validationCustom04"
                                    name="state"
                                    value={formData.state}
                                    onChange={onSelectChange}
                                    required
                                >
                                    <option value="">Choose...</option>
                                    <option value="CA">California</option>
                                    <option value="NY">New York</option>
                                    <option value="TX">Texas</option>
                                </Form.Select>
                                <Form.Feedback type="invalid">
                                    Please select a valid state.
                                </Form.Feedback>
                            </Column>
                            <Column col={Col.ColMd3}>
                                <Form.Label htmlFor="validationCustom05">Zip</Form.Label>
                                <Form.Control
                                    type={FormControlType.Text}
                                    id="validationCustom05"
                                    name="zip"
                                    value={formData.zip}
                                    onChange={onControlChange}
                                    required
                                />
                                <Form.Feedback type="invalid">
                                    Please provide a valid zip.
                                </Form.Feedback>
                            </Column>
                            <Column col={Col.Col12}>
                                <Form.Check
                                    type="checkbox"
                                    label="Agree to terms and conditions"
                                    id="invalidCheck"
                                    name="terms"
                                    checked={formData.terms}
                                    onChange={onControlChange}
                                    required
                                >
                                    <Form.Feedback type="invalid">
                                        You must agree before submitting.
                                    </Form.Feedback>
                                </Form.Check>
                            </Column>
                            <Column col={Col.Col12}>
                                <Button type={ButtonType.Submit} color={ButtonColor.Primary}>
                                    Submit form
                                </Button>
                            </Column>
                        </Row>
                    </Form>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {validationCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Advanced Custom Validation */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3" id="custom-validation">
                Advanced Custom Validation
            </Heading>
            <Text className="mb-4">
                For complex validation requirements, you can implement custom validation logic with separate error state management. This approach provides:
            </Text>
            <ul className="mb-4">
                <li><strong>Field-level validation</strong> - Validate individual fields on blur</li>
                <li><strong>Real-time error feedback</strong> - Clear errors as the user types</li>
                <li><strong>Custom validation rules</strong> - Implement any business logic (regex, length checks, cross-field validation)</li>
                <li><strong>Progressive error display</strong> - Show errors only after the user interacts with a field</li>
            </ul>

            <Heading el="h3" className="fw-medium fs-5 mt-4 mb-2">
                Implementation Pattern
            </Heading>
            <Text className="mb-4">
                This pattern uses three key event handlers to provide the best user experience:
            </Text>
            <ul className="mb-4">
                <li><code>handleChange</code> - Updates field value and clears errors as user types</li>
                <li><code>handleBlur</code> - Validates the field when user leaves it (tabs away or clicks elsewhere)</li>
                <li><code>handleSubmit</code> - Validates all fields before form submission</li>
            </ul>

            <Heading el="h3" className="fw-medium fs-5 mt-4 mb-2">
                Live Example
            </Heading>
            <Text className="mb-4">
                Try interacting with the form below. Click into a field, then click away to trigger validation. Notice how errors appear only after you leave the field, and disappear as you start typing.
            </Text>

            <CodeSnippet>
                <CodePreview>
                    {advancedSuccess && (
                        <div className="alert alert-success mb-3" role="status">
                            Form submitted successfully (demo reset). See the browser console for the payload.
                        </div>
                    )}
                    <Form onSubmit={handleAdvancedSubmit}>
                        <Row className="g-3">
                            <Column col={Col.ColMd6}>
                                <Form.Label htmlFor="advancedFirstName">First name</Form.Label>
                                <Form.Control
                                    type={FormControlType.Text}
                                    id="advancedFirstName"
                                    name="fName"
                                    value={advancedFormData.fName}
                                    onChange={handleAdvancedChange}
                                    onBlur={handleAdvancedBlur}
                                    className={advancedErrors.fName ? 'is-invalid' : ''}
                                />
                                {advancedErrors.fName && (
                                    <div className="invalid-feedback d-block">
                                        {advancedErrors.fName}
                                    </div>
                                )}
                            </Column>
                            <Column col={Col.ColMd6}>
                                <Form.Label htmlFor="advancedLastName">Last name</Form.Label>
                                <Form.Control
                                    type={FormControlType.Text}
                                    id="advancedLastName"
                                    name="lName"
                                    value={advancedFormData.lName}
                                    onChange={handleAdvancedChange}
                                    onBlur={handleAdvancedBlur}
                                    className={advancedErrors.lName ? 'is-invalid' : ''}
                                />
                                {advancedErrors.lName && (
                                    <div className="invalid-feedback d-block">
                                        {advancedErrors.lName}
                                    </div>
                                )}
                            </Column>
                            <Column col={Col.ColMd6}>
                                <Form.Label htmlFor="advancedPhone">Phone number</Form.Label>
                                <Form.Control
                                    type={FormControlType.Text}
                                    id="advancedPhone"
                                    name="phone"
                                    value={advancedFormData.phone}
                                    onChange={handleAdvancedChange}
                                    onBlur={handleAdvancedBlur}
                                    placeholder="1234567890"
                                    className={advancedErrors.phone ? 'is-invalid' : ''}
                                />
                                {advancedErrors.phone && (
                                    <div className="invalid-feedback d-block">
                                        {advancedErrors.phone}
                                    </div>
                                )}
                                <Form.Text muted>Enter 10 digits without formatting</Form.Text>
                            </Column>
                            <Column col={Col.ColMd6}>
                                <Form.Label htmlFor="advancedEmail">Email</Form.Label>
                                <Form.Control
                                    type={FormControlType.Email}
                                    id="advancedEmail"
                                    name="email"
                                    value={advancedFormData.email}
                                    onChange={handleAdvancedChange}
                                    onBlur={handleAdvancedBlur}
                                    placeholder="email@example.com"
                                    className={advancedErrors.email ? 'is-invalid' : ''}
                                />
                                {advancedErrors.email && (
                                    <div className="invalid-feedback d-block">
                                        {advancedErrors.email}
                                    </div>
                                )}
                            </Column>
                            <Column col={Col.Col12}>
                                <Button type={ButtonType.Submit} color={ButtonColor.Primary}>
                                    Submit form
                                </Button>
                            </Column>
                        </Row>
                    </Form>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {advancedValidationCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h3" className="fw-medium fs-5 mt-4 mb-2">
                Key Benefits
            </Heading>
            <ul className="mb-4">
                <li><strong>Better UX</strong> - Errors appear progressively, not all at once</li>
                <li><strong>Flexible</strong> - Easy to add custom validation rules (regex patterns, API calls, etc.)</li>
                <li><strong>Clean State</strong> - Errors automatically clear when user starts fixing them</li>
                <li><strong>Scalable</strong> - Pattern works for forms of any size</li>
            </ul>

        </Container>
    );
}



const validationCode = `const [validated, setValidated] = useState(false);
const [formData, setFormData] = useState({
  firstName: 'Mark',
  lastName: 'Otto',
  username: '',
  city: '',
  state: '',
  zip: '',
  terms: false
});

const handleFormFieldChange = (
  e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
) => {
  const { name, value, type } = e.target;
  if (type === "checkbox") {
    const checked = (e.target as HTMLInputElement).checked;
    setFormData((prev) => ({ ...prev, [name]: checked }));
  } else {
    setFormData((prev) => ({ ...prev, [name]: value }));
  }
};

const onControlChange =
  handleFormFieldChange as ChangeEventHandler<HTMLInputElement>;
const onSelectChange =
  handleFormFieldChange as ChangeEventHandler<HTMLSelectElement>;

const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
  event.preventDefault();
  const form = event.currentTarget;

  if (form.checkValidity() === false) {
    event.stopPropagation();
  } else {
    console.log('Form submitted:', formData);
  }

  setValidated(true);
};

<Form validated={validated} onSubmit={handleSubmit} noValidate>
  <Row className="g-3">
    <Column col={Col.ColMd4}>
      <Form.Label htmlFor="validationCustom01">First name</Form.Label>
      <Form.Control
        type={FormControlType.Text}
        id="validationCustom01"
        name="firstName"
        value={formData.firstName}
        onChange={onControlChange}
        required
      />
      <Form.Feedback type="valid">Looks good!</Form.Feedback>
      <Form.Feedback type="invalid">First name is required.</Form.Feedback>
    </Column>
    
    <Column col={Col.ColMd4}>
      <Form.Label htmlFor="validationCustom02">Last name</Form.Label>
      <Form.Control
        type={FormControlType.Text}
        id="validationCustom02"
        name="lastName"
        value={formData.lastName}
        onChange={onControlChange}
        required
      />
      <Form.Feedback type="valid">Looks good!</Form.Feedback>
      <Form.Feedback type="invalid">Last name is required.</Form.Feedback>
    </Column>
    
    <Column col={Col.ColMd4}>
      <Form.Label htmlFor="validationCustomUsername">Username</Form.Label>
      <Form.Control
        type={FormControlType.Text}
        id="validationCustomUsername"
        name="username"
        value={formData.username}
        onChange={onControlChange}
        required
      />
      <Form.Feedback type="invalid">
        Please choose a username.
      </Form.Feedback>
    </Column>
    
    <Column col={Col.ColMd6}>
      <Form.Label htmlFor="validationCustom03">City</Form.Label>
      <Form.Control
        type={FormControlType.Text}
        id="validationCustom03"
        name="city"
        value={formData.city}
        onChange={onControlChange}
        required
      />
      <Form.Feedback type="invalid">
        Please provide a valid city.
      </Form.Feedback>
    </Column>
    
    <Column col={Col.ColMd3}>
      <Form.Label htmlFor="validationCustom04">State</Form.Label>
      <Form.Select
        id="validationCustom04"
        name="state"
        value={formData.state}
        onChange={onSelectChange}
        required
      >
        <option value="">Choose...</option>
        <option value="CA">California</option>
        <option value="NY">New York</option>
        <option value="TX">Texas</option>
      </Form.Select>
      <Form.Feedback type="invalid">
        Please select a valid state.
      </Form.Feedback>
    </Column>
    
    <Column col={Col.ColMd3}>
      <Form.Label htmlFor="validationCustom05">Zip</Form.Label>
      <Form.Control
        type={FormControlType.Text}
        id="validationCustom05"
        name="zip"
        value={formData.zip}
        onChange={onControlChange}
        required
      />
      <Form.Feedback type="invalid">
        Please provide a valid zip.
      </Form.Feedback>
    </Column>
    
    <Column col={Col.Col12}>
      <Form.Check
        type="checkbox"
        label="Agree to terms and conditions"
        id="invalidCheck"
        name="terms"
        checked={formData.terms}
        onChange={onControlChange}
        required
      >
        <Form.Feedback type="invalid">
          You must agree before submitting.
        </Form.Feedback>
      </Form.Check>
    </Column>
    
    <Column col={Col.Col12}>
      <Button type={ButtonType.Submit} color={ButtonColor.Primary}>
        Submit form
      </Button>
    </Column>
  </Row>
</Form>`;

const advancedValidationCode = `// Validation function with custom business rules
const validateForm = (values) => {
  let errors = {};
  
  if (!values.fName.trim()) {
    errors.fName = "First name is required.";
  }
  
  if (!values.lName.trim()) {
    errors.lName = "Last name is required.";
  }
  
  if (!values.phone.trim()) {
    errors.phone = "Phone number is required.";
  } else if (!/^\\d{10}$/.test(values.phone.replace(/\\D/g, ''))) {
    errors.phone = "Please enter a valid 10-digit phone number.";
  }
  
  // Add more validation rules as needed
  return errors;
};

// Component with validation state
const OrderForm = () => {
  // Error state - tracks validation errors for each field
  const [errors, setErrors] = useState({
    phone: "",
    fName: "",
    lName: "",
  });

  // Form data state
  const [inputValues, setInputValues] = useState({
    fName: "",
    lName: "",
    phone: "",
  });

  // Handle input changes - Updates value and clears error
  const handleChange = (e) => {
    const { name, value } = e.target;
    setInputValues(prev => ({
      ...prev,
      [name]: String(value)
    }));
    // Clear error as user types
    setErrors(prev => ({
      ...prev,
      [name]: "",
    }));
  };

  // Handle input blur - Validates field when user leaves it
  const handleBlur = (e) => {
    const { name, value } = e.target;
    const currentValues = { ...inputValues, [name]: value };
    const fieldErrors = validateForm(currentValues);
    
    if (fieldErrors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: fieldErrors[name],
      }));
    }
  };

  // Handle form submission - Validates all fields
  const handleSubmit = (e) => {
    e.preventDefault();
    const formErrors = validateForm(inputValues);
    
    if (Object.keys(formErrors).length === 0) {
      console.log("Form submitted successfully:", inputValues);
      // Proceed with your submission logic
    } else {
      setErrors(formErrors); // Display all errors
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Form.Group>
        <Form.Label htmlFor="firstName">First name</Form.Label>
        <Form.Control
          type={FormControlType.Text}
          id="firstName"
          name="fName"
          value={inputValues.fName}
          onChange={handleChange}
          onBlur={handleBlur}
          className={errors.fName ? 'is-invalid' : ''}
        />
        {errors.fName && (
          <div className="invalid-feedback d-block">
            {errors.fName}
          </div>
        )}
      </Form.Group>

      <Form.Group>
        <Form.Label htmlFor="lastName">Last name</Form.Label>
        <Form.Control
          type={FormControlType.Text}
          id="lastName"
          name="lName"
          value={inputValues.lName}
          onChange={handleChange}
          onBlur={handleBlur}
          className={errors.lName ? 'is-invalid' : ''}
        />
        {errors.lName && (
          <div className="invalid-feedback d-block">
            {errors.lName}
          </div>
        )}
      </Form.Group>

      <Form.Group>
        <Form.Label htmlFor="phone">Phone</Form.Label>
        <Form.Control
          type={FormControlType.Text}
          id="phone"
          name="phone"
          value={inputValues.phone}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="(555) 123-4567"
          className={errors.phone ? 'is-invalid' : ''}
        />
        {errors.phone && (
          <div className="invalid-feedback d-block">
            {errors.phone}
          </div>
        )}
      </Form.Group>

      <Button type="submit" color={ButtonColor.Primary}>
        Submit
      </Button>
    </Form>
  );
};`;

export default ValidationPage;
